import styled from '@emotion/styled'
import { css, Interpolation, SerializedStyles, Theme } from '@emotion/react'

import { Controller, UseControllerProps, FieldValues, FieldPath, PathValue } from 'react-hook-form'
import ReactSelect, { GroupBase, OptionsOrGroups, StylesConfig } from 'react-select'
import InputBase from '@src/components/inputs/InputBase'

interface SelectProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
	label?: string
	isError?: boolean
	disabled?: boolean
	errorMsg?: string
	wrapperStyle?: Interpolation<Theme>
	labelStyle?: Interpolation<Theme>
	controllerProps: UseControllerProps<TFieldValues, TName>
	selectProps: {
		options: OptionsOrGroups<
			PathValue<TFieldValues, TName>,
			GroupBase<PathValue<TFieldValues, TName>>
		>
		styles?:
			| StylesConfig<
					PathValue<TFieldValues, TName>,
					false,
					GroupBase<PathValue<TFieldValues, TName>>
			  >
			| undefined
	}
}

const Select = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	label,
	isError,
	disabled,
	errorMsg,
	wrapperStyle,
	labelStyle,
	controllerProps,
	selectProps,
}: SelectProps<TFieldValues, TName>) => {
	const selectCustomStyle = {
		control: () => ({
			display: 'flex',
			border: isError ? '1px solid red' : '1px solid #92959e',
			borderRadius: '0.4rem',
			fontSize: '1.2rem',
		}),
		indicatorSeparator: () => ({
			border: 'none',
		}),
	}
	return (
		<InputBase css={[wrapperBasicStyle, wrapperStyle]}>
			{label !== undefined && <InputLabel css={labelStyle}>{label}</InputLabel>}
			<Controller
				{...controllerProps}
				render={({ field }) => (
					<ReactSelect
						styles={selectCustomStyle}
						{...field}
						{...selectProps}
						css={{ width: '100%' }}
						placeholder="선택"
					/>
				)}
			/>
			{errorMsg && <ErrorMsg>{errorMsg}</ErrorMsg>}
		</InputBase>
	)
}

const wrapperBasicStyle = css`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 1rem 0;
`

const InputLabel = styled.label`
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	font-size: 1.3rem;
	font-weight: 600;
	line-height: 2.2rem;
	width: 13rem;
	height: 3.4rem;
`

const ErrorMsg = styled.div`
	padding-top: 0.4rem;
	color: #de0000;
	font-size: 1.2rem;
	line-height: 2rem;
	font-weight: 600;
`

export default Select
